<template>
  <div class="locations-cargo" :class="{ 'one-location': isOneLocation }">
    <vz-switcher
      v-model="currentBlock"
      :items="blockItems"
      public-style
      class="mb-10"
    />
    <public-cargo-grid :items="currentBlockItems" />
    <vz-button
      link
      variant="primary"
      class="text-decoration-underline fs-18 ml-auto"
      @click="showAll = !showAll"
    >
      {{ showAllText }}
    </vz-button>
  </div>
</template>

<script setup lang="ts">
import VzSwitcher from '~/components/ui-kit/vz-switcher.vue';
import {SwitchItem} from '~/types/types';
import VzButton from '~/components/ui-kit/vz-button.vue';
import PublicGetFullInfo from '~/components/public/public-get-full-info.vue';
import PublicCargoGrid from '~/components/public/public-cargo-grid.vue';

const props = defineProps({
  isOneLocation: {
    type: Boolean,
    default: false
  }
})

const currentBlock = ref<'individual'|'business'|'service'>(props.isOneLocation ? 'individual' : 'business')
const showAll = ref(false)

const individualBlocks = computed(() => {
  const items = [
    {
      title: props.isOneLocation ? 'Перевозка вещей' : 'Перевезти вещи',
      imgUrl: '/images/pages/cargo/things.png',
      to: { name: 'shippingtypes-personalitemstransportation' }
    },
    {
      title: 'Перевезти автомобиль',
      imgUrl: '/images/pages/cargo/auto.png',
      to: {}
    },
    {
      title: props.isOneLocation ? 'Перевозка прицепа' : 'Перевезти прицеп',
      imgUrl: '/images/pages/cargo/trailer.png',
      to: { name: 'shippingtypes-trailer' }
    },
    {
      title: 'Перевезти мебель',
      imgUrl: '/images/pages/cargo/furniture.png',
      to: { name: 'shippingtypes-furniture' }
    },
  ]
  
  return props.isOneLocation
  ? [
      ...items,
      {
        title: 'Перевезти мотоцикл',
        imgUrl: '/images/pages/cargo/motorcycle.png',
        to: { name: 'shippingtypes-motorcycletransportation' }
      },
      {
        title: 'Перевозки по межгороду',
        imgUrl: '/images/pages/cargo/map.png',
        to: { name: 'terminals' },
      },
    ]
  : [
      {
        title: 'Отправить посылку',
        imgUrl: '/images/pages/cargo/parcel.png',
        to: { name: 'shippingtypes-goods' }
      },
      ...items,
    ]
})

const businessBlocks = computed(() => {
  const items = [
    {
      title: props.isOneLocation ? 'Перевозка сборных грузов' : 'Доставить сборные грузы',
      imgUrl: '/images/pages/cargo/complex.png',
      to: { name: 'delivery-russia' },
    },
    {
      title: props.isOneLocation ? 'Перевозка негабаритного груза' : 'Доставить негабаритный груз',
      imgUrl: '/images/pages/cargo/oversize.png',
      to: { name: 'shippingtypes-oversized' }
    },
  ]

  return props.isOneLocation
  ? [
      ...items,
      {
        title: 'Доставка от двери до двери',
        imgUrl: '/images/pages/cargo/pointer.png',
        to: { name: 'address-delivery' },
      },
      {
        title: 'Доставка до маркетплейсов',
        imgUrl: '/images/pages/cargo/marketplace.png',
        to: { name: 'networkshops' },
      },
      {
        title: 'Доставка для интернет-магазинов',
        imgUrl: '/images/pages/cargo/networkshop.png',
        to: { name: 'online-store' },
      },
    ]
  : [
      ...items,
      {
        title: 'Доставить в Москву',
        imgUrl: '/images/pages/cargo/capital.png',
        to: { name: 'order-create-locations', params: { locations: 'moskva' } },
      },
    ]
})

const serviceBlocks = [
  {
    title: 'Доставка в маркетплейсы',
    imgUrl: '/images/pages/cargo/marketplace.png',
    to: { name: 'networkshops' }
  },
  {
    title: 'Перевозка манипулятором',
    imgUrl: '/images/pages/cargo/manipulator.png',
    to: { name: 'shippingtypes-special-equipment' }
  },
  {
    title: 'Паллетирование',
    imgUrl: '/images/pages/wrapping/palletizing-wrapping.png',
    to: { name: 'wrapping-palletising' }
  },
  {
    title: 'Обрешетка',
    imgUrl: '/images/pages/wrapping/hard-package-wrapping.png',
    to: { name: 'wrapping-hard-box' }
  },
  {
    title: 'Хранение',
    imgUrl: '/images/pages/cargo/storage.png',
    to: { name: 'safe-custody' }
  },
]

const blockItems = computed((): SwitchItem[] => {
return props.isOneLocation 
    ? 
    [
      { value: 'individual', title: 'Частным лицам' },
      { value: 'business', title: 'Бизнесу' },
    ]
    : 
    [ 
      { value: 'business', title: 'Бизнесу' },
      { value: 'individual', title: 'Людям' },
      { value: 'service', title: 'Услуги' } 
    ]
})

const currentBlockItems = computed(() => {
  if (showAll.value) {
    return props.isOneLocation
      ? individualBlocks.value.concat(businessBlocks.value)
      : individualBlocks.value.concat(businessBlocks.value).concat(serviceBlocks)
  }
  return currentBlock.value === 'individual'
    ? individualBlocks.value
    : currentBlock.value === 'business'
      ? businessBlocks.value
      : serviceBlocks
})

const showAllText = computed(() => {
  return showAll.value ? 'Меньше' : 'Все грузоперевозки'
})
</script>

<style lang="scss" scoped>
.locations-cargo {
  :deep(.vz-switcher) {
    .vz-tooltip {
      @media screen and (max-width: $pub-xs) {
        margin-top: 10px !important;
      }
    }
  }

  &.one-location {
    :deep(.public-cargo-grid) {
      grid-template-columns: repeat(3, 1fr);

      @media screen and (max-width: $pub-md) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media screen and (max-width: $pub-xs) {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>
